/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, type ReactElement, useState } from 'react';

import { css, jsx, keyframes, cssMap as unboundedCssMap } from '@compiled/react';
import throttle from 'lodash/throttle';
import { FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import VideoPauseIcon from '@atlaskit/icon/core/video-pause';
import VideoPlayIcon from '@atlaskit/icon/core/video-play';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';
import { type ItemDimensions, useWidthAndSpacingCalculations } from '@atlassian/company-hub-common';

import { type Card } from '../../../../types';
import { CardsAIDisclaimer } from '../ai-disclaimer';

import { messages } from './messages';

// Design wants to always have a 16px gap between cards in thumbnail navigation,
// Therefore, don't want to modify the calculations logic, so just updating here for now
export const MIN_GAP_BETWEEN_CARDS = 16;
export const MAX_GAP_BETWEEN_CARDS = 16;

const wrapperStyles = css({
	overflowX: 'hidden',
	display: 'flex',

	// We render the card set twice for autoscroll, so applying a hover animation pause directly to one group won't stop the other.
	// Instead, we detect hover on the entire parent/container to pause the animation for all child elements at once.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover > div': {
		animationPlayState: 'paused',
	},
});

const pausedStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		animationPlayState: 'paused',
	},
});

const scrollAnimation = keyframes({
	'0% ': {
		transform: 'translateX(0)',
	},
	'100%': {
		transform: 'translateX(-100%)',
	},
});

const styles = cssMap({
	iconBackgroundStyles: {
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.full'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	iconPadding: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	cardsGroup: {
		display: 'flex',
	},
	container: {
		// WidthObserver with absolute only respect width of the nearest non-flex positioned ancestor.
		// In this case, the wrapper is the nearest non-flex positioned ancestor.
		position: 'relative',
	},
});

const unboundedStyles = unboundedCssMap({
	wrapperStyles: {
		flex: 1,
		paddingBlock: token('space.025'),
		position: 'relative',
	},
});

const animationStyles = css({
	animation: `${scrollAnimation} linear infinite`,
});

const shortDuration = css({
	animationDuration: '40s',
});

const mediumDuration = css({
	animationDuration: '50s',
});

const longDuration = css({
	animationDuration: '110s',
});

type AutoscrollCardsProps = {
	cardDimensions: ItemDimensions;
	cards: Card[];
	numberOfCards: number;
	renderCard: (card: Card) => ReactElement;
	showAIDisclaimer?: boolean;
	testId?: string;
};

export const AutoscrollCards = ({
	numberOfCards,
	cardDimensions,
	cards,
	renderCard,
	showAIDisclaimer,
	testId,
}: AutoscrollCardsProps) => {
	// there are 2 ways to pause the autoscroll
	// 1. Hover over a card. Should resume back to speed when unhovered
	// 2. Pause button click. Should resume when clicked again. This is implemented to meet a11y standards
	const [isPaused, setIsPaused] = useState(false);
	const handlePauseClick = () => setIsPaused(!isPaused);

	const { calculations, setCurrentCardsGridWidth } = useWidthAndSpacingCalculations({
		numberOfCards,
		cardDimensions,
	});

	const throttledSetWidth = throttle(setCurrentCardsGridWidth, 200);

	const { cardWidthOnFullRow, isFirstRowOverflown: isAnimationEnabled } = calculations;

	const renderCardsGroup = () => (
		<div
			css={[
				styles.cardsGroup,
				isAnimationEnabled && animationStyles,
				numberOfCards <= 5 ? shortDuration : numberOfCards <= 10 ? mediumDuration : longDuration,
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			style={{ gap: `${MIN_GAP_BETWEEN_CARDS}px`, paddingRight: `${MIN_GAP_BETWEEN_CARDS}px` }}
		>
			{cards.map((card, cardIndex) => {
				return (
					<Fragment key={cardIndex}>
						<div
							css={[unboundedStyles.wrapperStyles]}
							style={{
								minWidth: `${cardWidthOnFullRow}px`,
								maxWidth: `${cardWidthOnFullRow}px`,
							}}
						>
							{renderCard(card)}
						</div>
					</Fragment>
				);
			})}
		</div>
	);

	// we are making a duplicate call to renderCardsGroup to make the infinte scroll experience more seamless
	// duplicate call is only required when animation is enabled, meaning there is overflow of cards than the number of cards a row can fit
	// in which case, no autoscroll will take effect
	return (
		<Stack testId={testId} xcss={styles.container} space="space.075">
			<div css={[wrapperStyles, isPaused && pausedStyles]} data-testid="cards-autoscroll-wrapper">
				{renderCardsGroup()}
				{isAnimationEnabled && renderCardsGroup()}

				<WidthObserver setWidth={throttledSetWidth} offscreen />
			</div>
			{isAnimationEnabled && (
				<Inline alignInline="start">
					<Box xcss={styles.iconPadding}>
						<Inline space="space.075" alignBlock="center">
							<Box xcss={styles.iconBackgroundStyles}>
								<IconButton
									shape="circle"
									icon={isPaused ? VideoPlayIcon : VideoPauseIcon}
									label={
										isPaused ? (
											<FormattedMessage {...messages.play} />
										) : (
											<FormattedMessage {...messages.pause} />
										)
									}
									onClick={handlePauseClick}
								/>
							</Box>
							{showAIDisclaimer && <CardsAIDisclaimer />}
						</Inline>
					</Box>
				</Inline>
			)}
		</Stack>
	);
};
